import { useContext, useId, type ReactNode } from "react";
import { Chart, type ChartData, type ChartSeries } from "../Chart.tsx";
import { TimeRangeContext } from "../../state/time-range.tsx";

/**
 * ChartCard — a titled panel wrapping the Chart wrapper (observability-ui task 1.3; design
 * §Components and Interfaces #2).
 *
 * It is the kit's time-series surface: a labelled region with a heading (title + optional unit +
 * optional legend) over the shared `.obs-panel` surface, a chart body that reuses
 * `components/Chart.tsx`, and an optional footer. The Chart wrapper already owns everything that
 * makes a chart trustworthy — the always-present accessible data-table alternative, honest
 * empty/loading states, theme-driven colours (rebuild on `data-theme` change), and the synced
 * crosshair — so ChartCard adds only the panel chrome around it and forwards the data/series through
 * unchanged. There is no new charting code here and no Netdata/GPL code is copied; this is native
 * panel chrome over our existing wrapper.
 *
 * Live/paused: the chart freezes with the app-wide time-range (design 12.2, FR-13 AC2). ChartCard
 * does not fetch or window data itself — a paused screen passes the frozen window's data and the
 * chart shows it frozen. When a `TimeRangeProvider` is present, ChartCard surfaces the current
 * live/paused state in its header as a text+aria label (never colour alone, design 12.4) so the
 * operator can see at a glance whether the panel is tracking "now" or frozen. Rendered outside a
 * provider (e.g. a lone card or a test), it simply omits that indicator rather than throwing — the
 * kit is reusable in isolation.
 *
 * Theme-aware throughout: every colour comes from the theme tokens via `observability.css`; there
 * are no hard-coded colours and no permanently-dark surface (Requirement 2.1, 2.3).
 */
export interface ChartCardProps {
  /** Panel title; also the chart's accessible name and the region's label. */
  readonly title: string;
  /** Optional unit shown next to the title (e.g. "req/s", "ms"). */
  readonly unit?: string;
  /** Aligned chart data `[xs, ...ys]` forwarded to the Chart wrapper. */
  readonly data: ChartData;
  /** One descriptor per plotted y column, forwarded to the Chart wrapper. */
  readonly series: readonly ChartSeries[];
  /** Show the chart's polite loading status instead of the plot (data still arriving). */
  readonly loading?: boolean;
  /** Override the chart's empty-state cause when the window has no data. */
  readonly empty?: string;
  /** Fixed chart height in CSS px; width tracks the card. Forwarded to the Chart wrapper. */
  readonly height?: number;
  /** Optional footer content (e.g. a source note or a last-updated time). */
  readonly footer?: ReactNode;
}

export function ChartCard(props: ChartCardProps): ReactNode {
  const headingId = useId();
  // Read the time-range context directly (not via `useTimeRange`, which throws without a provider):
  // the kit must render in isolation. `null` → no provider → omit the live/paused indicator.
  const timeRange = useContext(TimeRangeContext);
  const liveState =
    timeRange === null ? null : timeRange.live ? "live" : "paused";

  return (
    <section className="obs-panel obs-chart-card" role="region" aria-labelledby={headingId}>
      <header className="obs-panel__header">
        <h3 className="obs-panel__title" id={headingId}>
          {props.title}
          {props.unit !== undefined && props.unit.length > 0 && (
            <span className="obs-panel__unit"> ({props.unit})</span>
          )}
        </h3>
        {liveState !== null && (
          <span
            className="obs-chart-card__live"
            data-state={liveState}
            role="status"
            aria-label={
              liveState === "live"
                ? "Live: tracking now"
                : "Paused: window frozen"
            }
          >
            {liveState === "live" ? "Live" : "Paused"}
          </span>
        )}
      </header>

      <div className="obs-panel__body">
        {/* Only forward optional props that are actually set: the base tsconfig uses
            `exactOptionalPropertyTypes`, so passing an explicit `undefined` to an optional prop is a
            type error. Spreading a conditionally-built object keeps the omitted-vs-undefined
            distinction the Chart wrapper's prop types expect. */}
        <Chart
          title={props.title}
          data={props.data}
          series={props.series}
          {...(props.loading !== undefined ? { loading: props.loading } : {})}
          {...(props.empty !== undefined ? { emptyCause: props.empty } : {})}
          {...(props.height !== undefined ? { height: props.height } : {})}
        />
      </div>

      {props.footer !== undefined && (
        <footer className="obs-panel__footer">{props.footer}</footer>
      )}
    </section>
  );
}
